// "Ver perfil" (sidebar web, pedido 2026-09-03: "no visual web... tirar isso
// e coloca o nome: Ver perfil, ai consegue editar a imagem do perfil visual
// igual [n]o perfil no app apk pwa") — mesmo conteúdo da tela "Meu perfil"
// do PWA (MeuPerfilScreen em mobile-app.tsx): foto real (AvatarEditor,
// componente compartilhado) + troca da própria senha, só que como Dialog em
// vez de tela cheia. Troca de senha usa o mesmo padrão: confirma a senha
// ATUAL com um login real antes de trocar.
import { passwordPolicyError } from "@/lib/password-policy";
import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/auth-context";
import { useUser, useUpdateUser } from "@/hooks/queries";
import { authService } from "@/services/auth.service";
import { AvatarEditor } from "@/components/user-avatar-editor";

export function UserProfileDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user, gpsDriveUser } = useAuth();
  const { data: fullUser } = useUser(open ? user?.id : undefined);
  const updateUser = useUpdateUser();
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confNovaSenha, setConfNovaSenha] = useState("");
  const [showAtual, setShowAtual] = useState(false);
  const [showNova, setShowNova] = useState(false);
  const [saving, setSaving] = useState(false);

  const resetPasswordFields = () => {
    setSenhaAtual("");
    setNovaSenha("");
    setConfNovaSenha("");
  };

  const handleSavePassword = async () => {
    if (!user) return;
    if (!senhaAtual || !novaSenha) {
      toast.error("Preencha a senha atual e a nova senha");
      return;
    }
    const policyError = passwordPolicyError(gpsDriveUser?.accountType, novaSenha);
    if (policyError) {
      toast.error("Senha fraca", { description: policyError });
      return;
    }
    if (novaSenha !== confNovaSenha) {
      toast.error("As senhas não coincidem", { description: "Confira a confirmação da nova senha." });
      return;
    }
    if (!fullUser) {
      toast.error("Não foi possível carregar seus dados agora. Tente novamente.");
      return;
    }
    setSaving(true);
    try {
      await authService.login(user.email, senhaAtual);
    } catch {
      toast.error("Senha atual incorreta");
      setSaving(false);
      return;
    }
    try {
      await updateUser.mutateAsync({ id: user.id, data: { ...fullUser, password: novaSenha } });
      toast.success("Senha alterada com sucesso");
      resetPasswordFields();
    } catch (err) {
      toast.error("Não foi possível trocar a senha", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetPasswordFields();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Meu perfil</DialogTitle>
          <DialogDescription>Sua foto e sua senha de entrada — só você tem acesso.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center py-2">
          <AvatarEditor traccarUserId={user?.id} size={96} />
          <p className="mt-3 text-sm font-semibold">{user?.name}</p>
          <p className="text-xs text-muted-foreground">{user?.email}</p>
        </div>

        <div className="space-y-3 border-t border-border pt-4">
          <div>
            <h3 className="text-sm font-bold">Trocar senha de entrada</h3>
            <p className="text-xs text-muted-foreground">Essa senha é só sua — ninguém mais tem acesso a ela.</p>
          </div>
          <PasswordField
            label="Senha atual"
            value={senhaAtual}
            onChange={setSenhaAtual}
            show={showAtual}
            onToggleShow={() => setShowAtual((s) => !s)}
          />
          <PasswordField
            label="Nova senha"
            value={novaSenha}
            onChange={setNovaSenha}
            show={showNova}
            onToggleShow={() => setShowNova((s) => !s)}
          />
          <PasswordField
            label="Confirmar nova senha"
            value={confNovaSenha}
            onChange={setConfNovaSenha}
            show={showNova}
            onToggleShow={() => setShowNova((s) => !s)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button onClick={handleSavePassword} disabled={saving}>
            {saving ? "Salvando…" : "Salvar nova senha"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  show,
  onToggleShow,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          autoComplete="new-password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pr-10"
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label={show ? "Ocultar senha" : "Mostrar senha"}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
